import React, { useCallback, useRef, useEffect, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { BsPersonCircle, BsFileEarmarkPdf, BsPrinter } from "react-icons/bs";
import { MdEdit, MdDelete, MdAttachFile } from "react-icons/md";
import { HiOutlineArrowLeft } from "react-icons/hi";
import CurrencyFormat from "react-currency-format";
import moment from "moment";
import { useReactToPrint } from "react-to-print";

import { accountService } from "../../services/accountService";
import GeneralModal from "../../helpers/GeneralModal";
import { createModal } from "react-modal-promise";
import LogicContext from "../../Context/LogicContext";
import PaymentReceiveScroll from "./PaymentReceiveScroll";
import PaymentBulkAction from "./PaymentBulkAction";
import FilePreviewModal from "../../helpers/FilePreviewModal";

import { formatCustomDate } from "../../helpers/formatCustomDate";
import { formatCustomNumber } from "../../helpers/formatCustomNumber";
import StatusBadge from "../../generals/StatusBadge";

export default function PaymentReceivedDetailsPage() {
  const navigate = useNavigate();
  const { payment_ref_id } = useParams();
  const { pathname } = useLocation();
  const logicContext = React.useContext(LogicContext);

  const [paymentData, setPaymentData] = useState({});
  const [selectedList, setSelectedList] = useState([]);

  const componentRef = useRef();
  const myPromiseModal = createModal(GeneralModal);

  const currModuleId = pathname.split("/")[1];
  const currModuleName = pathname.split("/")[2];
  const currUserMenu = pathname.split("/")[3];

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    documentTitle: `Payment_Receipt_${paymentData?.Payment_Ref_No || "unknown"}`,
  });

  const fetchPaymentDetails = useCallback(async () => {
    try {
      const response = await accountService.getPaymentReceiveDetails(payment_ref_id);
      if (response?.data?.length) {
        setPaymentData(response.data[0]);
      }
    } catch (err) {
      logicContext.showToast("Failed to load payment details", "error");
    }
  }, [payment_ref_id, logicContext]);

  useEffect(() => {
    document.title = "Payment Received | Details";
    fetchPaymentDetails();
  }, [payment_ref_id]);

  const goBack = () => navigate(-1);

  const confirmAndEdit = async () => {
    const confirmed = await myPromiseModal({
      title: "Modify Payment",
      body: "Would you like to edit this payment record?",
      showInput: false,
    });
    if (confirmed?.decision) {
      navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/add-new`);
    }
  };

  const confirmAndDelete = async () => {
    const confirmed = await myPromiseModal({
      title: "Delete Payment",
      body: `Are you sure you want to delete payment ${paymentData?.Payment_Ref_No}?`,
      showInput: false,
    });
    if (confirmed?.decision) {
      try {
        const res = await accountService.deletePaymentReceive(paymentData.Payment_Ref_No);
        if (res?.data?.[0]?.message_text) {
          logicContext.showToast(res.data[0].message_text, "success");
          navigate(-1);
        }
      } catch (err) {
        logicContext.showToast("Could not delete payment", "error");
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      {/* Header / Actions Bar */}
      <div className="sticky top-0 z-10 bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button
                onClick={goBack}
                className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
              >
                <HiOutlineArrowLeft size={20} />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">
                Payment Receipt
              </h1>
              {paymentData?.Payment_Ref_No && (
                <span className="text-sm text-gray-500 font-mono">
                  #{paymentData.Payment_Ref_No}
                </span>
              )}
            </div>

            <div className="flex items-center gap-2">
              {selectedList.length > 0 && (
                <PaymentBulkAction
                  onPaymentModeSelect={() => {}}
                  onDepositSelect={() => {}}
                  performBulkActions={() => {}}
                />
              )}

              <div className="flex items-center gap-1.5">
                <button
                  title="Print Receipt"
                  onClick={handlePrint}
                  className="p-2.5 rounded-lg hover:bg-gray-100 text-gray-700 transition-colors"
                >
                  <BsPrinter size={20} />
                </button>

                <button
                  title="Edit Payment"
                  onClick={confirmAndEdit}
                  className="p-2.5 rounded-lg hover:bg-gray-100 text-blue-600 hover:text-blue-700 transition-colors"
                >
                  <MdEdit size={20} />
                </button>

                <button
                  title="Delete Payment"
                  onClick={confirmAndDelete}
                  className="p-2.5 rounded-lg hover:bg-gray-100 text-rose-600 hover:text-rose-700 transition-colors"
                >
                  <MdDelete size={20} />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6">
        <div className="flex flex-col lg:flex-row gap-6">
          {/* Left sidebar - list */}
          <div className="lg:w-80 xl:w-96 flex-shrink-0">
            <div className="bg-white rounded-xl border shadow-sm overflow-hidden sticky top-20">
              <PaymentReceiveScroll
                onSelection={(list) => setSelectedList(list.filter(i => i.checked))}
                viewItemDetails={(id) =>
                  navigate(`/${currModuleId}/${currModuleName}/${currUserMenu}/details/${id}`)
                }
              />
            </div>
          </div>

          {/* Main content */}
          <div className="flex-1">
            <div ref={componentRef} className="bg-white rounded-xl border shadow-sm p-8 print:shadow-none print:border-0">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-6 pb-8 border-b">
                <div>
                  <div className="flex items-center gap-3">
                    <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center">
                      <BsPersonCircle className="w-9 h-9 text-blue-600" />
                    </div>
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Payment Receipt</h2>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {paymentData?.Payment_Ref_No || "—"}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="text-right">
                  <div className="text-sm font-medium text-gray-900">
                    Worldbay Technologies Ltd
                  </div>
                  <div className="text-xs text-gray-600 mt-1 space-y-0.5">
                    <p>TIN: 16311833-0001  |  VAT: YBV060021065153  |  RC 1065153</p>
                    <p className="italic">
                      Plot 4b Chief Augustine Anozie Street, Lekki Phase 1, Lagos
                    </p>
                  </div>
                </div>
              </div>

              {/* Details Grid */}
              <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-6 text-sm">
                <div className="space-y-5">
                  <div>
                    <dt className="text-gray-600 font-medium">Date</dt>
                    <dd className="mt-1 font-medium text-gray-900">
                      {formatCustomDate(paymentData?.Transaction_Date) || "—"}
                    </dd>
                  </div>

                  <div>
                    <dt className="text-gray-600 font-medium">Customer</dt>
                    <dd className="mt-1 font-medium text-gray-900">
                      {paymentData?.Customer_Full_Name || "—"}
                    </dd>
                  </div>

                  <div>
                    <dt className="text-gray-600 font-medium">Description</dt>
                    <dd className="mt-1 text-gray-800">
                      {paymentData?.Deposit_Remark || "—"}
                    </dd>
                  </div>

                  <div>
                    <dt className="text-gray-600 font-medium">Payment Mode</dt>
                    <dd className="mt-1 font-medium text-gray-900">
                      {paymentData?.Deposit_Method_Name || "—"}
                    </dd>
                  </div>
                </div>

                <div className="space-y-5 md:text-right">
                  <div>
                    <dt className="text-gray-600 font-medium">Amount Received</dt>
                    <dd className="mt-1 text-xl font-bold text-emerald-600">
                      ₦ {formatCustomNumber(paymentData?.Deposit_Amount) || "0.00"}
                    </dd>
                  </div>

                  <div>
                    <dt className="text-gray-600 font-medium">Charge Fee</dt>
                    <dd className="mt-1 font-medium text-gray-900">
                      ₦ {formatCustomNumber(paymentData?.Charge_Amount) || "0.00"}
                    </dd>
                  </div>

                  <div>
                    <dt className="text-gray-600 font-medium">Recurring Type</dt>
                    <dd className="mt-1 font-medium text-gray-900">
                      {paymentData?.Recuring_Type_Name || "—"}
                    </dd>
                  </div>

                  <div>
                    <dt className="text-gray-600 font-medium">Status</dt>
                    <dd className="mt-1">
                      <StatusBadge
                        statusId={paymentData?.Transaction_Status_Id}
                        statusName={paymentData?.Transaction_Status_Name}
                      />
                    </dd>
                  </div>
                </div>
              </div>

              {/* Footer message */}
              <div className="mt-12 pt-8 border-t text-center text-sm text-gray-500">
                <p className="font-medium text-gray-700">Thank you for your payment!</p>
                <p className="mt-1">
                  For inquiries: <span className="text-blue-600">support@worldbaytech.com</span>
                </p>
              </div>
            </div>

            {/* Attachment */}
            {paymentData?.Attached_File_Name === "Yes" && (
              <div className="mt-6 bg-white rounded-xl border shadow-sm p-6">
                <div className="flex items-center gap-3 mb-4">
                  <MdAttachFile className="text-blue-600" size={24} />
                  <h3 className="font-semibold text-gray-900">
                    Attached Document
                  </h3>
                </div>
                <div className="text-sm text-gray-600 mb-3">
                  {paymentData?.Payment_Ref_No}.{paymentData?.Attached_File_Format}
                </div>
                <FilePreviewModal
                  file={`${paymentData?.Payment_Ref_No}.${paymentData?.Attached_File_Format}`}
                />
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}